import React from "react"
import { motion } from "framer-motion"
import { Loader2 } from "lucide-react"
import { twMerge } from "tailwind-merge"

import type { PlacesType } from "react-tooltip"

type ExtendsProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onDrag" | "onDragEnd" | "onDragStart" | "onAnimationStart" | "onAnimationEnd"
>

type VariantsOutline = "outline-danger"

type VariantType =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "yellow"
  | "ghost-danger"
  | "success"
  | VariantsOutline
type SizeType = "xs" | "sm" | "md" | "lg"
type ButtonTypes = "button" | "iconButton"

interface ButtonProps extends ExtendsProps {
  children?: React.ReactNode
  buttonTypes?: ButtonTypes
  tooltipPlace?: PlacesType
  variant?: VariantType
  loading?: boolean
  size?: SizeType
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  children,
  className = "",
  disabled,
  buttonTypes = "button",
  tooltipPlace = "top",
  ...props
}) => {
  const { title, ...restProps } = props
  const baseClasses =
    "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
  const variants = {
    primary: "bg-legacy-blue enabled:hover:bg-legacy-navy text-white focus:ring-legacy-blue",
    secondary:
      "bg-legacy-light enabled:hover:bg-legacy-blue text-legacy-dark dark:bg-legacy-light/20 dark:enabled:hover:bg-legacy-blue dark:text-legacy-cream focus:ring-legacy-light",
    outline:
      "border-2 border-legacy-blue text-legacy-blue enabled:hover:bg-legacy-blue enabled:hover:text-white focus:ring-legacy-blue dark:border-legacy-blue dark:text-slate-400 dark:enabled:hover:bg-legacy-blue dark:enabled:hover:text-white",
    ghost:
      "text-legacy-blue enabled:hover:bg-legacy-light/20 focus:ring-legacy-blue dark:text-legacy-blue dark:enabled:hover:bg-legacy-light/10",
    danger: "bg-red-500 enabled:hover:bg-red-600 text-white focus:ring-red-500",
    yellow: "bg-yellow-500 enabled:hover:bg-yellow-600 text-white focus:ring-yellow-500",
    "ghost-danger":
      "text-red-500 enabled:hover:bg-red-100 focus:ring-red-500 dark:text-red-500 dark:enabled:hover:bg-red-900/20",
    success:
      "bg-emerald-500 enabled:hover:bg-emerald-600 text-white focus:ring-emerald-500 dark:bg-emerald-500 dark:enabled:hover:bg-emerald-600 dark:text-white dark:focus:ring-emerald-500",
    "outline-danger":
      "border-2 border-red-500 text-red-500 enabled:hover:bg-red-500 enabled:hover:text-white focus:ring-red-500 dark:border-red-500 dark:text-red-500 dark:enabled:hover:bg-red-500 dark:enabled:hover:text-white",
  }
  const sizes = {
    xs: "px-2 py-1 text-xs",
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  }

  const dataAttributes: any = {}
  if (title) {
    dataAttributes["data-tooltip-id"] = "t4ever-tooltip"
    dataAttributes["data-tooltip-content"] = title
    dataAttributes["data-tooltip-place"] = tooltipPlace
  }

  return (
    <motion.button
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={twMerge(
        baseClasses,
        variants[variant],
        sizes[size],
        className,
        buttonTypes === "iconButton" ? "p-0 h-8 w-8" : "",
      )}
      disabled={disabled || loading}
      {...dataAttributes}
      {...restProps}
    >
      {loading && <Loader2 size={20} className="mr-2 animate-spin" />}
      {children}
    </motion.button>
  )
}

export default Button
